import type {
  PersistedClient,
  Persister,
} from '@tanstack/react-query-persist-client';
import { Platform } from 'react-native';
import { createMMKV } from 'react-native-mmkv';

import { Logger } from '@/utils/logging';

export const storage = createMMKV({
  id: 'squire/flagship',
  mode: 'multi-process',
  readOnly: false,
});

export const storageKeys = {
  selectedSearch: 'selectedSearch',
  lastSearchedItems: 'lastSearchedItems',
  customerId: 'customerId',
  appleUserId: 'appleUserId',
  authToken: 'token',
  appointmentConfirmationCode: 'appointmentConfirmationCode',
  bookingContext: 'bookingContext',
  cartId: 'cartId',
  rudderstackAnonymusUserId: 'rudderstackAnonymusUserId',
  trackingSessionUrl: 'trackingSessionUrl',
  selectedEnvironment: '@dev/selected_environment',
  isDevMenuEnabled: '@dev/is_dev_menu_enabled',
  // DevMenu theme override. Dark mode is not supported app-wide yet, so
  // `_layout` forces light UNLESS a dev has explicitly picked a theme here.
  devMenuTheme: '@dev/theme',
  // SQR-14053: DevMenu override for the `bnpl-experiment` LD flag. Holds a
  // BnplCheckoutVariant string, or is absent to defer to LaunchDarkly.
  bnplVariantOverride: '@dev/bnpl_variant_override',
  // SQR-17027: DevMenu mock for `breakdown.deposit` until the API serves it.
  // Holds a DepositMockMode string, or is absent for no mock.
  depositMock: '@dev/deposit_mock',
  userLanguage: 'userLanguage',
  guestUserInfo: 'guestUserInfo',
  authReturnPath: 'authReturnPath',
  refreshToken: 'refreshToken',
  accessTokenExpiresAt: 'accessTokenExpiresAt',
  refreshTokenExpiresAt: 'refreshTokenExpiresAt',
  npsNextEligibleAt: 'npsNextEligibleAt',
  hasSeenReviewInterstitial: 'hasSeenReviewInterstitial',
  lastSuccessfulAppointmentCount: 'lastSuccessfulAppointmentCount',
  googleAdsAttribution: 'googleAdsAttribution',
  rwgAttribution: 'rwgAttribution',
  bnplPending: 'bnplPending',
  cashAppPending: 'cashAppPending',
  cashAppGiftCardPending: 'cashAppGiftCardPending',
  bnplReopenConfirm: 'bnplReopenConfirm',
  cashAppReopenConfirm: 'cashAppReopenConfirm',
  giftCardReopenConfirm: 'giftCardReopenConfirm',
  ldCheckoutContext: 'ldCheckoutContext',
  googleSignInEmail: 'googleSignInEmail',
  // Whether Apple Pay is the customer's default payment method. Mirrors the
  // ios-flagship `isApplePayDefaultPaymentMethod` UserDefaults flag: stored
  // locally and mutually exclusive with a saved card's backend default. Used as
  // a key PREFIX scoped per customer (`${applePayIsDefault}:${customerId}`) so
  // the preference doesn't leak across accounts on a shared device.
  applePayIsDefault: 'applePayIsDefault',
  // SQR-14629: marks the one-time legacy-session import as done, independent of
  // sign-in state, so a later logout cannot make the migration resurrect the
  // legacy session on the next launch.
  legacyMigrationDone: 'legacyMigrationDone',
  hasDismissedLocationSearchNotice: 'hasDismissedLocationSearchNotice',
};

const MMKV_PERSIST_KEY = 'flagship';

/**
 * Creates a React Query persister that uses MMKV storage for native platforms.
 * @param storeKey - The storage key for the React Query client data
 * @returns A persister object compatible with React Query's persistence API
 */
function mmkvStoragePersistor(storeKey: string = MMKV_PERSIST_KEY) {
  return {
    persistClient: async (client: PersistedClient) => {
      try {
        storage.set(storeKey, JSON.stringify(client));
      } catch (error) {
        Logger.error('MMKV persist client failed', { error, storeKey });
        throw error;
      }
    },
    restoreClient: async () => {
      try {
        const serialized = storage.getString(storeKey);

        if (!serialized) {
          Logger.console('MMKV restore client: no data found', { storeKey });
          return undefined;
        }
        return JSON.parse(serialized) as PersistedClient;
      } catch (error) {
        Logger.error('MMKV restore client failed', { error, storeKey });
        throw error;
      }
    },
    removeClient: async () => {
      try {
        storage.remove(storeKey);
      } catch (error) {
        Logger.error('MMKV remove client failed', { error, storeKey });
        throw error;
      }
    },
  } satisfies Persister;
}

const IDB_DB_NAME = 'squire-flagship-db';
const IDB_STORE_NAME = 'query-cache';
const IDB_KEY = 'flagship';

/**
 * Opens the database at its current version. A database left at a version
 * without the object store never fires `onupgradeneeded` again, so it is
 * reopened one version up to create the store.
 */
function openIndexedDB(version?: number): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request =
      version === undefined
        ? indexedDB.open(IDB_DB_NAME)
        : indexedDB.open(IDB_DB_NAME, version);
    request.onerror = () => reject(request.error);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(IDB_STORE_NAME)) {
        db.createObjectStore(IDB_STORE_NAME);
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      if (db.objectStoreNames.contains(IDB_STORE_NAME)) {
        resolve(db);
        return;
      }
      if (version !== undefined) {
        db.close();
        reject(new Error('IndexedDB object store missing after upgrade'));
        return;
      }
      const nextVersion = db.version + 1;
      db.close();
      openIndexedDB(nextVersion).then(resolve, reject);
    };
  });
}

function runRequest<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  makeRequest: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(IDB_STORE_NAME, mode);
    const request = makeRequest(transaction.objectStore(IDB_STORE_NAME));
    request.onerror = () => reject(request.error);
    transaction.onabort = () => reject(transaction.error);
    request.onsuccess = () => resolve(request.result);
  });
}

/**
 * The IndexedDB store only caches React Query data, so any failure (no
 * IndexedDB, a connection the browser closed, an aborted transaction) is
 * logged as a warning and treated as an empty cache instead of thrown.
 */
async function withIndexedDB<T>(
  operation: string,
  fallback: T,
  run: (db: IDBDatabase) => Promise<T>,
): Promise<T> {
  if (typeof indexedDB === 'undefined') return fallback;

  let db: IDBDatabase | undefined;
  try {
    db = await openIndexedDB();
    return await run(db);
  } catch (error) {
    Logger.warn(`IndexedDB ${operation} failed`, { error, key: IDB_KEY });
    return fallback;
  } finally {
    db?.close();
  }
}

/**
 * Creates a React Query persister that uses IndexedDB for web platforms.
 * Uses JSON serialization to avoid DataCloneError from non-cloneable values (e.g. functions).
 * @returns A persister object compatible with React Query's persistence API
 */
export function createIndexedDBPersistor(): Persister {
  return {
    persistClient: async (client: PersistedClient) => {
      await withIndexedDB('persist client', undefined, async (db) => {
        await runRequest(db, 'readwrite', (store) =>
          store.put(JSON.stringify(client), IDB_KEY),
        );
      });
    },
    restoreClient: () =>
      withIndexedDB('restore client', undefined, async (db) => {
        const data = await runRequest<string | undefined>(
          db,
          'readonly',
          (store) => store.get(IDB_KEY),
        );
        if (!data) {
          Logger.console('IndexedDB restore client: no data found', {
            key: IDB_KEY,
          });
          return undefined;
        }
        return JSON.parse(data) as PersistedClient;
      }),
    removeClient: async () => {
      await withIndexedDB('remove client', undefined, async (db) => {
        await runRequest(db, 'readwrite', (store) => store.delete(IDB_KEY));
      });
    },
  };
}

/**
 * Creates a platform-specific persister for React Query.
 * Uses IndexedDB on web and MMKV on native platforms.
 * @returns A persister object compatible with React Query's persistence API
 */
function createPersistor(): Persister {
  if (Platform.OS === 'web') {
    // For web indexeddb is used for storage
    // Because indexeddb is more reliable than localStorage
    return createIndexedDBPersistor();
  }
  // For mobile mmkv is used for storage
  return mmkvStoragePersistor();
}

export const persistor = createPersistor();
